import React, { useState, useRef, useEffect } from 'react';
import {
  Bell,
  X,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Info,
  Settings,
  Trash2,
  Check,
  Filter,
  Clock,
  User,
  FileText,
  Shield,
  Server,
  GitBranch,
  Activity,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DesktopNotificationsMenuSection } from '../pwa/DesktopNotifications';
import { cn } from '../../utils/cn';
import { useNotifications, type Notification } from '../../contexts/NotificationContext';
import {
  INBOX_NOTIFICATION_CATEGORIES,
  INBOX_NOTIFICATION_PRIORITIES,
  type InboxNotificationCategory,
  type InboxNotificationPriority,
  type InboxNotificationType,
} from '../../services/inboxApi';
import { formatDistanceToNow } from 'date-fns';

// Phase C.104: display labels keyed by the same tuples so dropdowns
// don't have to hand-list each option. Title-casing rule (`SSR`,
// `Infrastructure` rather than `infra`) makes it not-quite-a-pure
// derivation, so explicit maps stay tighter than a humanize() helper.
const CATEGORY_LABEL: Record<InboxNotificationCategory, string> = {
  system: 'System',
  import: 'Import',
  project: 'Project',
  user: 'User',
  security: 'Security',
  infra: 'Infrastructure',
  ssr: 'SSR',
};

const PRIORITY_LABEL: Record<InboxNotificationPriority, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

type FilterValue = 'all' | 'unread' | 'read';

// Phase C.91: exhaustive lookup tables keyed by the inboxApi type
// unions. Replaces three switch ladders that each carried a
// `default` fallback — under strict typing those defaults were
// unreachable, and the switches were yet another place to update
// when a new category appeared (Phase C.13 had to amend the
// category switch when 'infra' / 'ssr' landed). With `Record<K, V>`
// TS yells at edit time if any key is missing.
const TYPE_ICON: Record<InboxNotificationType, React.ReactNode> = {
  success: <CheckCircle className="w-4 h-4 text-green-500" />,
  warning: <AlertTriangle className="w-4 h-4 text-yellow-500" />,
  error: <AlertCircle className="w-4 h-4 text-red-500" />,
  info: <Info className="w-4 h-4 text-blue-500" />,
};

const CATEGORY_ICON: Record<InboxNotificationCategory, React.ReactNode> = {
  system: <Server className="w-3 h-3" />,
  import: <FileText className="w-3 h-3" />,
  project: <FileText className="w-3 h-3" />,
  user: <User className="w-3 h-3" />,
  security: <Shield className="w-3 h-3" />,
  // Phase C.13: infra (drift / scrape outage) and ssr (error
  // threshold / anomaly / connection error) deserve their own
  // glyphs so the bell list is scannable by category.
  infra: <GitBranch className="w-3 h-3" />,
  ssr: <Activity className="w-3 h-3" />,
};

const PRIORITY_COLOR: Record<InboxNotificationPriority, string> = {
  critical: 'text-red-600',
  high: 'text-orange-500',
  medium: 'text-blue-500',
  low: 'text-gray-500',
};

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
  onTogglePreferences?: () => void;
  /** Element (e.g. the bell button) whose clicks must not count as "outside" — it toggles itself. */
  anchorRef?: React.RefObject<HTMLElement | null>;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({
  isOpen,
  onClose,
  onTogglePreferences,
  anchorRef,
}) => {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearAll,
    removeNotification,
    hasMore,
    loadingMore,
    loadMore,
    isConnected,
  } = useNotifications();
  const navigate = useNavigate();

  // Phase C.28: route based on URL shape. Server-pushed notifications
  // pass paths like `/incidents/:id` — these should navigate in-app,
  // not pop a new tab. Absolute URLs (http://, https://) get a new
  // tab as before. Also closes the dropdown so the user lands on
  // their destination, not staring at the bell list.
  const followActionUrl = (url: string) => {
    if (/^https?:\/\//i.test(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      onClose();
      navigate(url);
    }
  };

  const [filter, setFilter] = useState<FilterValue>('all');
  const [categoryFilter, setCategoryFilter] = useState<'all' | InboxNotificationCategory>('all');
  const [priorityFilter, setPriorityFilter] = useState<'all' | InboxNotificationPriority>('all');
  // Phase C.37: client-side text search across loaded rows. Doesn't
  // hit the server (no full-text index yet) — operates on the
  // current loaded page + any subsequently loaded via "Load older".
  // For typical inbox sizes (<200 visible) that's plenty.
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (anchorRef?.current?.contains(target)) return;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, anchorRef]);

  // Filter notifications
  const trimmedQuery = searchQuery.trim().toLowerCase();
  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'unread' && notification.read) return false;
    if (filter === 'read' && !notification.read) return false;
    if (categoryFilter !== 'all' && notification.category !== categoryFilter) return false;
    if (priorityFilter !== 'all' && notification.priority !== priorityFilter) return false;
    // Phase C.37: substring match on title or message. Empty query
    // = no filter. Case-insensitive.
    if (trimmedQuery) {
      const haystack = `${notification.title} ${notification.message}`.toLowerCase();
      if (!haystack.includes(trimmedQuery)) return false;
    }
    return true;
  });

  // Phase C.41: bucket by date so the list scans top-down by recency
  // without operators having to read timestamps. Today/Yesterday/Older
  // is the common social-feed split — anything else is over-engineering
  // for a 60-day TTL inbox.
  const groupedNotifications: Array<{ label: string; items: Notification[] }> = (() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    const today: Notification[] = [];
    const yesterday: Notification[] = [];
    const older: Notification[] = [];
    for (const n of filteredNotifications) {
      const ts = new Date(n.timestamp).getTime();
      if (ts >= startOfToday.getTime()) today.push(n);
      else if (ts >= startOfYesterday.getTime()) yesterday.push(n);
      else older.push(n);
    }
    const groups: Array<{ label: string; items: Notification[] }> = [];
    if (today.length) groups.push({ label: 'Today', items: today });
    if (yesterday.length) groups.push({ label: 'Yesterday', items: yesterday });
    if (older.length) groups.push({ label: 'Older', items: older });
    return groups;
  })();

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // Phase C.28: prefer `actionUrl` (string) over legacy `actions`
    // (closure array). String form lets us route internal paths
    // through react-router so the user stays in the SPA.
    if (notification.actionUrl) {
      followActionUrl(notification.actionUrl);
      return;
    }
    if (notification.actions && notification.actions.length > 0) {
      notification.actions[0].action();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-lg ring-1 ring-slate-900/5 z-50 max-h-[32rem] flex flex-col overflow-hidden"
      role="dialog"
      aria-label="Notification Center"
    >
      {/* Header */}
      <div className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-slate-200 px-4">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
          {unreadCount > 0 && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-50 px-1.5 text-xs font-semibold text-rose-700 tabular-nums">
              {unreadCount}
            </span>
          )}
          <span
            className={cn('h-1.5 w-1.5 rounded-full', isConnected ? 'bg-emerald-500' : 'bg-slate-300')}
            title={isConnected ? 'Connected to real-time notifications' : 'Disconnected from real-time notifications'}
            aria-label={isConnected ? 'Real-time connected' : 'Real-time disconnected'}
          />
        </div>
        <div className="flex items-center gap-1">
          {onTogglePreferences && (
            <button
              type="button"
              onClick={onTogglePreferences}
              className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
              title="Notification settings"
              aria-label="Notification settings"
            >
              <Settings className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close notifications"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* This device's desktop (push) notifications — formerly a page-wide banner. */}
      <DesktopNotificationsMenuSection />

      {/* Filters */}
      <div className="p-3 border-b border-gray-100 space-y-2">
        {/* Phase C.37: text search across loaded rows. With TTL of
            60d an active inbox can hold hundreds of rows; "find me
            that one infra alert about cluster1a" is a real flow. */}
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search title or message…"
          className="w-full text-sm border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-gray-500" />
          {/* Phase C.104: dropdowns iterate the inboxApi tuples + a
              label map keyed by the same union. Adding a new
              category in `inboxApi.ts` populates the filter
              automatically; explicit cast on the setter replaces
              the previous `as any`. */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as FilterValue)}
            className="text-sm border border-gray-200 rounded px-2 py-1"
          >
            <option value="all">All</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </select>
          <select
            value={categoryFilter}
            onChange={(e) =>
              setCategoryFilter(e.target.value as 'all' | InboxNotificationCategory)
            }
            className="text-sm border border-gray-200 rounded px-2 py-1"
          >
            <option value="all">All Categories</option>
            {INBOX_NOTIFICATION_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABEL[c]}
              </option>
            ))}
          </select>
          <select
            value={priorityFilter}
            onChange={(e) =>
              setPriorityFilter(e.target.value as 'all' | InboxNotificationPriority)
            }
            className="text-sm border border-gray-200 rounded px-2 py-1"
          >
            <option value="all">All Priorities</option>
            {INBOX_NOTIFICATION_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABEL[p]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Actions */}
      {notifications.length > 0 && (
        <div className="flex items-center justify-between p-3 border-b border-gray-100">
          <div className="flex space-x-2">
            {unreadCount > 0 && (
              <button
                onClick={() =>
                  markAllAsRead(
                    categoryFilter !== 'all'
                      ? { category: categoryFilter as InboxNotificationCategory }
                      : undefined
                  )
                }
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
              >
                <Check className="w-3 h-3" />
                <span>
                  {/* Phase C.31: label clarifies the scope when filter
                      is active so the user can't accidentally wipe
                      every unread in their inbox. */}
                  {categoryFilter === 'all'
                    ? 'Mark all read'
                    : `Mark ${categoryFilter} read`}
                </span>
              </button>
            )}
          </div>
          <button
            onClick={() =>
              clearAll(
                categoryFilter !== 'all'
                  ? { category: categoryFilter as InboxNotificationCategory }
                  : undefined
              )
            }
            className="text-sm text-red-600 hover:text-red-800 flex items-center space-x-1"
            title="Removes only already-read notifications. Unread rows stay."
          >
            <Trash2 className="w-3 h-3" />
            {/* Phase C.32: label clarifies that unread stays and
                (when filter is active) scope matches the filter. */}
            <span>
              {categoryFilter === 'all'
                ? 'Clear read'
                : `Clear read ${categoryFilter}`}
            </span>
          </button>
        </div>
      )}

      {/* Notifications List */}
      <div className="flex-1 overflow-y-auto">
        {filteredNotifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500 space-y-3">
            <Bell className="w-12 h-12 mx-auto text-gray-300" />
            {notifications.length === 0 ? (
              <>
                {/* Phase C.50: actionable hint for the truly-empty
                    case. Most users with empty bell either haven't
                    opted into SSR/Infra alerts yet or are on a
                    fresh install. Pointing them to settings is
                    more useful than a dead-end "No notifications
                    yet" line. */}
                <p className="text-sm font-medium text-gray-600">No notifications yet</p>
                {onTogglePreferences && (
                  <p className="text-xs text-gray-500">
                    Critical alerts arrive automatically; SSR and
                    Infrastructure events require opt-in.{' '}
                    <button
                      type="button"
                      onClick={onTogglePreferences}
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      Open preferences
                    </button>
                  </p>
                )}
              </>
            ) : trimmedQuery ? (
              <>
                <p className="text-sm">
                  Nothing matches "<span className="font-mono">{trimmedQuery}</span>"
                </p>
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  Clear search
                </button>
              </>
            ) : (
              <p className="text-sm">No notifications match your filters</p>
            )}
          </div>
        ) : (
          <div>
            {groupedNotifications.map((group) => (
              <div key={group.label}>
                {/* Phase C.41: sticky date header per bucket. */}
                <div className="sticky top-0 z-10 bg-gray-50 px-4 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-100">
                  {group.label}
                </div>
                <div className="divide-y divide-gray-100">
            {group.items.map((notification) => (
              <div
                key={notification.id}
                className={cn(
                  'p-4 hover:bg-gray-50 cursor-pointer transition-colors',
                  !notification.read && 'bg-blue-50 border-l-4 border-l-blue-500'
                )}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {TYPE_ICON[notification.type]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className={cn(
                        'text-sm font-medium truncate',
                        !notification.read ? 'text-gray-900' : 'text-gray-700'
                      )}>
                        {notification.title}
                      </h4>
                      <div className="flex items-center space-x-1 ml-2">
                        <span className={cn('w-2 h-2 rounded-full', PRIORITY_COLOR[notification.priority])} />
                        {CATEGORY_ICON[notification.category]}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                      {notification.message}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        {/* Phase C.36: hover tooltip shows absolute
                            timestamp. Relative is fine for scanning,
                            but during an incident root-cause the
                            operator needs to correlate to log /
                            metrics timestamps. */}
                        <span title={new Date(notification.timestamp).toLocaleString()}>
                          {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
                        </span>
                        {notification.jobId && (
                          <span className="bg-gray-100 rounded px-1.5 py-0.5">
                            Job: {notification.jobId}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeNotification(notification.id);
                        }}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        aria-label="Remove notification"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                    
                    {/* Phase C.28: actionUrl button — preferred path
                        for server-pushed notifications. */}
                    {notification.actionUrl && (
                      <div className="flex space-x-2 mt-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!notification.read) {
                              markAsRead(notification.id);
                            }
                            followActionUrl(notification.actionUrl!);
                          }}
                          className="px-2 py-1 text-xs font-medium rounded transition-colors bg-blue-600 text-white hover:bg-blue-700"
                        >
                          {notification.actionLabel ?? 'Open'}
                        </button>
                      </div>
                    )}

                    {/* Legacy closure-array actions (local toasts) */}
                    {notification.actions && notification.actions.length > 0 && (
                      <div className="flex space-x-2 mt-2">
                        {notification.actions.map((action, index) => (
                          <button
                            key={index}
                            onClick={(e) => {
                              e.stopPropagation();
                              action.action();
                              if (!notification.read) {
                                markAsRead(notification.id);
                              }
                            }}
                            className={cn(
                              'px-2 py-1 text-xs font-medium rounded transition-colors',
                              action.variant === 'primary' && 'bg-blue-600 text-white hover:bg-blue-700',
                              action.variant === 'secondary' && 'bg-gray-200 text-gray-800 hover:bg-gray-300',
                              action.variant === 'danger' && 'bg-red-600 text-white hover:bg-red-700',
                              !action.variant && 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                            )}
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {filteredNotifications.length > 0 && (
        <div className="p-3 border-t border-gray-100 text-center space-y-2">
          <p className="text-xs text-gray-500">
            Showing {filteredNotifications.length} of {notifications.length} notifications
          </p>
          {hasMore && (
            <button
              type="button"
              onClick={() => void loadMore()}
              disabled={loadingMore}
              className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              {loadingMore ? 'Loading…' : 'Load older notifications'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
